import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { ListView } from "@/services/list-view";
import { Globe, Lock, Search, Shield, Users, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Checkbox } from "../ui";

interface ShareViewModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    view: ListView;
    onViewUpdate?: (view: ListView) => void;
}

interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

interface Role {
    id: number;
    name: string;
    sf_id: string;
    hierarchy: string;
}

interface PicklistsData {
    users: User[];
    roles: Role[];
}

export function ShareViewModal({ open, onOpenChange, view, onViewUpdate }: ShareViewModalProps) {
    const [sharingMode, setSharingMode] = useState<"private" | "protected" | "public">(view.sharing_mode || "private");
    const [allowEdit, setAllowEdit] = useState(view.allow_other_edit);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [picklists, setPicklists] = useState<PicklistsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [userSearch, setUserSearch] = useState("");
    const [roleSearch, setRoleSearch] = useState("");

    // Fetch picklists when modal opens
    useEffect(() => {
        if (open && !picklists) {
            fetchPicklists();
        }
    }, [open, picklists]);

    // Load existing share data when modal opens
    useEffect(() => {
        if (open && view.id && view.id !== "default") {
            fetchShares();
        }
    }, [open, view.id]);

    const fetchPicklists = async () => {
        try {
            const response = (await DataService.client.url("/crm/app/list-views/picklists/").get().json()) as PicklistsData;
            setPicklists(response);
        } catch (error) {
            console.error("Error fetching picklists:", error);
            toast.error("Failed to load users and roles");
        }
    };

    const fetchShares = async () => {
        try {
            const response = await DataService.client.url(`/crm/app/list-views/${view.id}/shares/`).get().json();

            if (Array.isArray(response) && response.length > 0) {
                const share = response[0];
                setSelectedUsers(share.users?.map((u: User) => u.id) || []);
                setSelectedRoles(share.roles?.map((r: Role) => r.id) || []);
            }
        } catch (error) {
            console.error("Error fetching shares:", error);
        }
    };

    const handleSave = async () => {
        if (!view.id || view.id === "default") return;

        setLoading(true);
        try {
            const shareData = {
                sharing_mode: sharingMode,
                allow_other_edit: allowEdit,
                user_ids: selectedUsers,
                role_ids: selectedRoles
            };

            // Update share
            await DataService.client.url(`/crm/app/list-views/${view.id}/share/`).post(shareData).json();

            toast.success("Sharing settings updated successfully");
            onOpenChange(false);

            // Update the view in parent component
            if (onViewUpdate) {
                const updatedView = { ...view, sharing_mode: sharingMode };
                onViewUpdate(updatedView);
            }
        } catch (error) {
            toast.error("Failed to update sharing settings");
            console.error("Error updating sharing settings:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUserToggle = (userId: number) => {
        setSelectedUsers(prev => (prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]));
    };

    const handleRoleToggle = (roleId: number) => {
        setSelectedRoles(prev => (prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]));
    };

    // Filter users and roles based on search
    const filteredUsers = useMemo(() => {
        if (!picklists?.users) return [];
        if (!userSearch.trim()) return picklists.users;

        return picklists.users.filter(
            user =>
                user.first_name.toLowerCase().includes(userSearch.toLowerCase()) ||
                user.last_name.toLowerCase().includes(userSearch.toLowerCase()) ||
                user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                user.username.toLowerCase().includes(userSearch.toLowerCase())
        );
    }, [picklists?.users, userSearch]);

    const filteredRoles = useMemo(() => {
        if (!picklists?.roles) return [];
        if (!roleSearch.trim()) return picklists.roles;

        return picklists.roles.filter(role => role.name.toLowerCase().includes(roleSearch.toLowerCase()) || (role.sf_id && role.sf_id.toLowerCase().includes(roleSearch.toLowerCase())));
    }, [picklists?.roles, roleSearch]);

    const getSharingModeDescription = (mode: string) => {
        switch (mode) {
            case "public":
                return "Everyone can see and use this view";
            case "protected":
                return "Only selected users and roles can see this view";
            case "private":
            default:
                return "Only you can see this view";
        }
    };

    const userShareDisabled = sharingMode === "private" || (sharingMode === "public" && !allowEdit);
    const roleShareDisabled = sharingMode !== "protected";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Share List View
                    </DialogTitle>
                    <DialogDescription>Configure who can access "{view.name}"</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Sharing Mode Selection */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <div className="space-y-3">
                            <Label>Sharing Mode</Label>
                            <Select value={sharingMode} onValueChange={(value: "private" | "protected" | "public") => setSharingMode(value)}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="private">
                                        <div className="flex items-center gap-2">
                                            <Lock className="h-4 w-4" />
                                            Private
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="protected">
                                        <div className="flex items-center gap-2">
                                            <Shield className="h-4 w-4" />
                                            Protected
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="public">
                                        <div className="flex items-center gap-2">
                                            <Globe className="h-4 w-4" />
                                            Public
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-sm text-gray-500">{getSharingModeDescription(sharingMode)}</p>
                        </div>

                        {sharingMode !== "private" && (
                            <div className="flex items-center gap-2">
                                <Checkbox checked={allowEdit} onCheckedChange={v => setAllowEdit(v === true)} />
                                <div className="space-y-1 leading-none">
                                    <Label>Allow edit</Label>
                                    <p className="text-sm text-gray-500">Allow selected users to edit this view</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Users and Roles Selection - Side by Side */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-6">
                            {/* Users Selection */}
                            <div className={`space-y-3 ${userShareDisabled ? "opacity-50" : ""}`}>
                                <div>
                                    <Label>Share with Users</Label>
                                    <p className="text-sm text-gray-500">
                                        {sharingMode === "private"
                                            ? "Only you can see and edit this view"
                                            : sharingMode === "public" && allowEdit
                                              ? "The selected users will be able to edit this view"
                                              : sharingMode === "public" && !allowEdit
                                                ? "All users will only be able to see this view"
                                                : sharingMode === "protected" && allowEdit
                                                  ? "The selected users will be able to edit this view"
                                                  : "The selected users will only be able to see this view"}
                                    </p>
                                </div>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <Input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-10" disabled={userShareDisabled} />
                                </div>
                                <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border p-3">
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map(user => (
                                            <div
                                                key={user.id}
                                                className={`flex items-center justify-between rounded px-2 py-1 transition-colors ${
                                                    selectedUsers.includes(user.id) ? "border border-blue-200 bg-blue-50" : "hover:bg-gray-50"
                                                } ${userShareDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                                                onClick={userShareDisabled ? undefined : () => handleUserToggle(user.id)}
                                            >
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">
                                                        {user.first_name} {user.last_name}
                                                    </span>
                                                    <span className="text-xs text-gray-500">{user.email}</span>
                                                </div>
                                                {selectedUsers.includes(user.id) && <Badge variant="secondary">Selected</Badge>}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="py-4 text-center text-sm text-gray-500">{userSearch.trim() ? "No users found" : "No users available"}</div>
                                    )}
                                </div>
                            </div>

                            {/* Roles Selection */}
                            <div className={`space-y-3 ${roleShareDisabled ? "opacity-50" : ""}`}>
                                <div>
                                    <Label>Share with Roles</Label>
                                    <p className="text-sm text-gray-500">
                                        {sharingMode === "private"
                                            ? "Only you can see and edit this view"
                                            : sharingMode === "public"
                                              ? "The selected roles will be able to see this view"
                                              : sharingMode === "protected"
                                                ? "The selected roles will be able to see this view"
                                                : ""}
                                    </p>
                                </div>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                    <Input placeholder="Search roles..." value={roleSearch} onChange={e => setRoleSearch(e.target.value)} className="pl-10" disabled={roleShareDisabled} />
                                </div>
                                <div className="max-h-60 space-y-2 overflow-y-auto rounded-md border p-3">
                                    {filteredRoles.length > 0 ? (
                                        filteredRoles.map(role => (
                                            <div
                                                key={role.id}
                                                className={`flex items-center justify-between rounded px-2 py-1 transition-colors ${
                                                    selectedRoles.includes(role.id) ? "border border-blue-200 bg-blue-50" : "hover:bg-gray-50"
                                                } ${roleShareDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                                                onClick={roleShareDisabled ? undefined : () => handleRoleToggle(role.id)}
                                            >
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">{role.name}</span>
                                                    {role.sf_id && <span className="text-xs text-gray-500">SF ID: {role.sf_id}</span>}
                                                </div>
                                                {selectedRoles.includes(role.id) && <Badge variant="secondary">Selected</Badge>}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="py-4 text-center text-sm text-gray-500">{roleSearch.trim() ? "No roles found" : "No roles available"}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Selected Summary */}
                        {(selectedUsers.length > 0 || selectedRoles.length > 0) && (
                            <div className={cn("space-y-2", userShareDisabled && roleShareDisabled ? "opacity-50" : "")}>
                                <Label>Selected</Label>
                                <div className="flex flex-wrap gap-2">
                                    {selectedUsers.map(userId => {
                                        const user = picklists?.users.find(u => u.id === userId);
                                        return user ? (
                                            <Badge key={`user-${userId}`} variant="outline" className={cn("flex items-center gap-1", userShareDisabled ? "opacity-50" : "")}>
                                                <Users className="h-3 w-3" />
                                                {user.first_name} {user.last_name}
                                                <X
                                                    className={`h-3 w-3 cursor-pointer`}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleUserToggle(userId);
                                                    }}
                                                />
                                            </Badge>
                                        ) : null;
                                    })}
                                    {selectedRoles.map(roleId => {
                                        const role = picklists?.roles.find(r => r.id === roleId);
                                        return role ? (
                                            <Badge key={`role-${roleId}`} variant="outline" className={cn("flex items-center gap-1", roleShareDisabled ? "opacity-50" : "")}>
                                                <Shield className="h-3 w-3" />
                                                {role.name}
                                                <X
                                                    className={`h-3 w-3 cursor-pointer`}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleRoleToggle(roleId);
                                                    }}
                                                />
                                            </Badge>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={loading}>
                        {loading ? "Saving..." : "Save Changes"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
